import {KnightState, MechanicKnightState, NestedReadonly, PlayerColor, TileEdgeType} from '@colonist/game-states'
import {MechanicValidator} from './MechanicValidator'
import {MapValidator} from '../core/map/MapValidator'
import {PlayerValidator} from '../core/PlayerValidator'
import {MechanicValidatorKnightPlayer} from './MechanicValidatorKnightPlayer'
import {MapStateDepthFirstSearch} from '../core/map/MapStateDepthFirstSearch'
import {TileEdge} from '../core/map/Tile'

export class MechanicValidatorKnight extends MechanicValidator {
    private readonly knightPlayerValidators: MechanicValidatorKnightPlayer[]

    constructor(playerValidators: PlayerValidator[], mapValidator: MapValidator, knightState: NestedReadonly<MechanicKnightState>) {
        super(playerValidators, mapValidator)

        this.knightPlayerValidators = []
        for(const playerColorStr in knightState) {
            const playerColor = Number(playerColorStr) as PlayerColor
            const knightPlayerState = knightState[playerColor]
            if(knightPlayerState == undefined) continue

            const playerValidator = this.playerValidators.get(playerColor)
            if(playerValidator == undefined) continue

            const knightPlayerValidator = new MechanicValidatorKnightPlayer(playerValidator, mapValidator, knightPlayerState)
            this.knightPlayerValidators.push(knightPlayerValidator)
        }
    }

    getKnightPlayerValidators(): MechanicValidatorKnightPlayer[] {
        return this.knightPlayerValidators
    }

    getKnightPlayerValidator(player: PlayerColor): MechanicValidatorKnightPlayer {
        const knightPlayerValidator = this.knightPlayerValidators.find((validator) => validator.playerColor == player)
        if(knightPlayerValidator === undefined) throw new Error(`No knightPlayerValidator for player ${player}`)

        return knightPlayerValidator
    }

    getAnyKnightAtLocation(cornerIndex: number): NestedReadonly<KnightState> | undefined {
        for(const knightPlayerValidator of this.knightPlayerValidators) {
            const knightAtLocation = knightPlayerValidator.getKnightAtLocation(cornerIndex)
            if(knightAtLocation === undefined) continue

            return knightAtLocation
        }

        return undefined
    }

    getTotalKnightStrength(): number {
        const playerKnightStrengths = this.knightPlayerValidators.map((knightPlayerValidator) => knightPlayerValidator.getPlayerKnightStrength())
        return playerKnightStrengths.reduce((x, acc) => x + acc)
    }

    getPlayersWithHighestKnightStrength(): PlayerColor[] {
        const playerKnightStrengths = this.knightPlayerValidators.map((knightPlayerValidator) => knightPlayerValidator.getPlayerKnightStrength())
        const highestPlayerKnightStrength = Math.max(...playerKnightStrengths)
        return this.getPlayersWithKnightStrength(highestPlayerKnightStrength)
    }

    getPlayersWithLowestKnightStrength(excludingPlayers: PlayerColor[] = []): PlayerColor[] {
        const includedKnightPlayerValidators = this.knightPlayerValidators.filter((knightPlayerValidator) => !excludingPlayers.includes(knightPlayerValidator.playerColor))
        const playerKnightStrengths = includedKnightPlayerValidators.map((knightPlayerValidator) => knightPlayerValidator.getPlayerKnightStrength())
        const lowestPlayerKnightStrength = Math.min(...playerKnightStrengths)
        return this.getPlayersWithKnightStrength(lowestPlayerKnightStrength, excludingPlayers)
    }

    whereCanPlayerRemoveKnight(player: PlayerColor): number[] {
        const knightPlayerState = this.getKnightPlayerValidator(player)
        const whereCanPlayerRemoveKnight: number[] = []
        for(const knight of knightPlayerState.getKnightStates()) {
            if(knight.locationCornerIndex == undefined) continue

            whereCanPlayerRemoveKnight.push(knight.locationCornerIndex)
        }
        return whereCanPlayerRemoveKnight
    }

    whereCanPlayerMoveKnight(knightState: KnightState, player: PlayerColor, includeKnightDisplacement: boolean): number[] {
        const allStartingEdgesToInitialSearch = this.getStartingEdgesForKnight(knightState)

        const allLocationsPlayerCanMoveKnight: number[] = []
        for(const startingEdge of allStartingEdgesToInitialSearch) {
            const mapDepthFirstSearch = this.runMapDepthFirstSearch(player, startingEdge)
            if(mapDepthFirstSearch == undefined) return []

            const potentialEnemyKnightLocations = mapDepthFirstSearch.interConnectedEnemyBuildings
            const whatKnightsCanBeDisplacedByKnight = includeKnightDisplacement ? this.whatKnightsCanPlayerDisplacedWithKnight(potentialEnemyKnightLocations, knightState) : []

            const emptyLocationsToMoveKnight = mapDepthFirstSearch.interConnectedEmptyNodes

            allLocationsPlayerCanMoveKnight.push(...whatKnightsCanBeDisplacedByKnight)
            allLocationsPlayerCanMoveKnight.push(...emptyLocationsToMoveKnight)
        }
        return allLocationsPlayerCanMoveKnight
    }

    private runMapDepthFirstSearch(player: PlayerColor, startingEdgeToInitialSearch: TileEdge): MapStateDepthFirstSearch | undefined {
        const ignoreMyBuildings = true
        const ignoreEnemyBuildings = false
        const roadAndShipEdgeMap = this.mapValidator.generateEdgeMapForPlayerAndPieces(player, [TileEdgeType.Road, TileEdgeType.Ship])
        const mapDepthFirstSearch = new MapStateDepthFirstSearch(player, roadAndShipEdgeMap, startingEdgeToInitialSearch, this.mapValidator, ignoreEnemyBuildings, ignoreMyBuildings)
        return mapDepthFirstSearch
    }

    private getStartingEdgesForKnight(knightState: KnightState): TileEdge[] {
        if(knightState.locationCornerIndex == undefined) return []

        const startingEdgesForKnight: TileEdge[] = []
        const touchingEdges = this.mapValidator.tileState.tileCorners[knightState.locationCornerIndex].hexCorner.touchingEdges()
        for(const touchingEdge of touchingEdges) {
            const tileEdge = this.mapValidator.tileState.getTileEdge(touchingEdge)
            if(tileEdge == undefined) continue
            if(tileEdge.owner != knightState.owner) continue
            if(tileEdge.type == undefined) continue

            startingEdgesForKnight.push(tileEdge)
        }
        return startingEdgesForKnight
    }

    private whatKnightsCanPlayerDisplacedWithKnight(potentialEnemyKnightLocations: number[], knightTakingAction: KnightState): number[] {
        const knightsThatCanBeDisplaced: number[] = []
        for(const potentialKnightLocation of potentialEnemyKnightLocations) {
            const knightAtLocation = this.getAnyKnightAtLocation(potentialKnightLocation)
            if(knightAtLocation == undefined) continue
            if(knightAtLocation.level >= knightTakingAction.level) continue

            knightsThatCanBeDisplaced.push(potentialKnightLocation)
        }
        return knightsThatCanBeDisplaced
    }

    private getPlayersWithKnightStrength(strength: number, excludingPlayers: PlayerColor[] = []): PlayerColor[] {
        const playersWithKnightStrength: PlayerColor[] = []
        for(const knightPlayerValidator of this.knightPlayerValidators) {
            if(knightPlayerValidator.getPlayerKnightStrength() !== strength) continue
            if(excludingPlayers.includes(knightPlayerValidator.playerColor)) continue

            playersWithKnightStrength.push(knightPlayerValidator.playerColor)
        }
        return playersWithKnightStrength
    }
}
